import React, {
  useCallback, useEffect, useState,
} from "react";

import { CheckCircleOutlined, MinusOutlined } from "@ant-design/icons";
import {
  Button, Input, Modal, Switch, Table,
} from "antd";
import { toast } from "react-toastify";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import {
  ENVIRONMENT_RESERVED_FIELD_KEYS,
  PRODUCT_RESERVED_FIELD_KEYS,
} from "../OnboardingForm/SubForms/FieldsForm/Components/EditableFields";

import { BLOCKED_KEY_REGEX, ALLOWED_CHAR_REGEX } from "../../../utils/common";
import { getJsonFieldName } from "../helpers";
import { Specstype } from "./utils";

import { getEnvironmentFields } from "../../../actions/envAnalytics";
import { updateSampleFields } from "../../../actions/onboarding";
import { getProductFields } from "../../../actions/reports";

import { ReactComponent as LoadingDots } from "../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/sample-submission/onboarding-update-edit.svg";

import "./UpdateFields.css";

const INITIAL_FIELD_VALUE_STATE = {
  [Specstype.Product]: {},
  [Specstype.Environment]: {},
};

const READ_ONLY_FIELDS = {
  [Specstype.Product]: ["received_date", "physical_category", "po", "sample_type", "sample_id"],
  [Specstype.Environment]: ["received_date", "physical_category", "swab_number", "zone", "section", "sample_type", "sample_id"],
};

export default function UpdateFields({
  proceedToUploadExcel,
  onClose,
  selectedType,
  handleSelectedType,
  fields,
  setFields,
  updatedLinkReport,
  setUpdatedLinkReport,
  // updatedLinkPattern,  // used by groupedFields (Task 3 Download Template will re-use)
  setUpdatedLinkPattern,
  updatedLinkPatternFields,
  setUpdatedLinkPatternFields,
}) {
  const [sampleIdFields, setSampleIdFields] = useState(INITIAL_FIELD_VALUE_STATE);
  const [fieldValue, setFieldValue] = useState(INITIAL_FIELD_VALUE_STATE);
  const [originalValueSnapshot, setOriginalValueSnapshot] = useState(INITIAL_FIELD_VALUE_STATE);
  const [fieldAutofillValues, setFieldAutofillValues] = useState(INITIAL_FIELD_VALUE_STATE);
  const [testAutofillValues, setTestAutofillValues] = useState(INITIAL_FIELD_VALUE_STATE);
  const [originalAutofillSnapshot, setOriginalAutofillSnapshot] = useState({
    fieldAutofill: INITIAL_FIELD_VALUE_STATE,
    testAutofill: INITIAL_FIELD_VALUE_STATE,
  });
  const [loading, setLoading] = useState(false);
  const [loadingOnSave, setLoadingOnSave] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [updateDiff, setUpdateDiff] = useState([]);
  const [fieldErrors, setFieldErrors] = useState(INITIAL_FIELD_VALUE_STATE);

  /**
   * Fetch product and environment fields using productfields/ and environmentfields/ apis
   */
  const fetchFields = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all([getProductFields(), getEnvironmentFields()]);
      const fieldsObj = {
        [Specstype.Product]: [],
        [Specstype.Environment]: [],
      };
      const fieldValueObj = { ...INITIAL_FIELD_VALUE_STATE };
      const fieldAutofillObj = { ...INITIAL_FIELD_VALUE_STATE };
      const testAutofillObj = { ...INITIAL_FIELD_VALUE_STATE };
      const sampleIdObj = { ...INITIAL_FIELD_VALUE_STATE };
      results.forEach((result, i) => {
        const type = i === 0 ? Specstype.Product : Specstype.Environment;
        if (result.success) {
          fieldsObj[type] = result.fields;
          fieldValueObj[type] = result.fields.reduce((acc, f) => {
            acc[f.json_field] = f.title_field;
            return acc;
          }, {});
          fieldAutofillObj[type] = result.fields.reduce((acc, f) => {
            acc[f.json_field] = f.field_autofill;
            return acc;
          }, {});
          testAutofillObj[type] = result.fields.reduce((acc, f) => {
            acc[f.json_field] = f.test_autofill;
            return acc;
          }, {});
          if (result.sampleid_fields) {
            sampleIdObj[type] = result.sampleid_fields;
          }
          if (result.link_report) {
            setUpdatedLinkReport(result.link_report);
          }
          if (result.link_pattern) {
            setUpdatedLinkPattern(result.link_pattern);
            setUpdatedLinkPatternFields(result.link_pattern_fields);
          }
        }
      });
      setFields(fieldsObj);
      setFieldValue(fieldValueObj);
      setOriginalValueSnapshot(fieldValueObj);
      setFieldAutofillValues(fieldAutofillObj);
      setTestAutofillValues(testAutofillObj);
      setSampleIdFields(sampleIdObj);
      setOriginalAutofillSnapshot({ fieldAutofill: fieldAutofillObj, testAutofill: testAutofillObj });
    } finally {
      setLoading(false);
    }
  }, [setFields, setUpdatedLinkPattern, setUpdatedLinkPatternFields, setUpdatedLinkReport]);

  /**
   * Call productfields/ and environmentfields/ apis for getting fields
   */
  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const getFieldError = (jsonField, value, allCurrentValues) => {
    const trimmed = (value || "").trim();
    const computedKey = getJsonFieldName(trimmed);
    if (!computedKey) return null;

    const reservedKeys = selectedType === Specstype.Product
      ? PRODUCT_RESERVED_FIELD_KEYS
      : ENVIRONMENT_RESERVED_FIELD_KEYS;
    if (reservedKeys.includes(computedKey)) return "Reserved field name";

    const hasDuplicate = Object.entries(allCurrentValues).some(([otherField, otherValue]) => {
      if (otherField === jsonField) return false;
      const otherKey = getJsonFieldName(otherValue || "");
      return otherKey && otherKey === computedKey;
    });
    if (hasDuplicate) return "Duplicate field";

    return null;
  };

  /** on key down in input, check if key is an invalid char, if yes, don't let user input key
   * call onKeyDown from props
   * @param {string} json_field json field value
   * @param {Event} e KeyDown event
   */
  const onKeyDown = (field) => (e) => {
  // Allow navigation/editing keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    if (BLOCKED_KEY_REGEX.test(e.key)) {
      e.preventDefault();
      setFieldErrors((prev) => ({
        ...prev,
        [selectedType]: {
          ...prev[selectedType],
          [field]: "Only alphabets, numbers, spaces, hyphens (-), and underscores (_) are allowed.",
        },
      }));
    }
  };

  /** on copy pasting the values in input fields, check if its an invalid char, if yes, don't let user copy paste the input
   * call onPaste from props
   * @param {string} json_field json field value
   * @param {Event} e KeyDown event
   */
  const onPaste = (field) => (e) => {
    const pastedText = e.clipboardData.getData("text");

    if (!ALLOWED_CHAR_REGEX.test(pastedText)) {
      e.preventDefault();

      setFieldErrors((prev) => ({
        ...prev,
        [selectedType]: {
          ...prev[selectedType],
          [field]: "Only alphabets, numbers, spaces, hyphens (-), and underscores (_) are allowed.",
        },
      }));
    }
  };

  /**
   * Handles spec type change
   * @param {"product" | "environment"} type spec type
   */
  const handleChangeType = (type) => {
    if (type === selectedType) return;
    // Restore unsaved values from current type when it's changed
    setFieldValue((state) => ({
      ...state,
      [selectedType]: originalValueSnapshot[selectedType],
    }));
    setFieldAutofillValues((s) => ({ ...s, [selectedType]: originalAutofillSnapshot.fieldAutofill[selectedType] }));
    setTestAutofillValues((s) => ({ ...s, [selectedType]: originalAutofillSnapshot.testAutofill[selectedType] }));
    setFieldErrors((prev) => ({ ...prev, [selectedType]: {} }));
    handleSelectedType(type);
    setIsEditing(false);
  };

  /**
   * Handles input value change
   * @param {*} field field input
   */
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    const newTypeValues = { ...fieldValue[selectedType], [field]: value };
    setFieldValue((state) => ({ ...state, [selectedType]: newTypeValues }));
    const errors = {};
    Object.keys(newTypeValues).forEach((jsonField) => {
      if (!READ_ONLY_FIELDS[selectedType].includes(jsonField)) {
        errors[jsonField] = getFieldError(jsonField, newTypeValues[jsonField], newTypeValues);
      }
    });
    setFieldErrors((prev) => ({ ...prev, [selectedType]: errors }));
  };

  const handleFieldAutofillChange = (jsonField, checked) => {
    setFieldAutofillValues((s) => ({
      ...s,
      [selectedType]: { ...s[selectedType], [jsonField]: checked ? "1" : "0" },
    }));
  };

  const handleTestAutofillChange = (jsonField, checked) => {
    setTestAutofillValues((s) => ({
      ...s,
      [selectedType]: { ...s[selectedType], [jsonField]: checked ? "1" : "0" },
    }));
  };

  /**
   * Restores all field values to their last-saved snapshot and exits edit mode
   */
  const handleCancelEdit = () => {
    setFieldValue((s) => ({ ...s, [selectedType]: originalValueSnapshot[selectedType] }));
    setFieldAutofillValues((s) => ({ ...s, [selectedType]: originalAutofillSnapshot.fieldAutofill[selectedType] }));
    setTestAutofillValues((s) => ({ ...s, [selectedType]: originalAutofillSnapshot.testAutofill[selectedType] }));
    setFieldErrors((prev) => ({ ...prev, [selectedType]: {} }));
    setIsEditing(false);
  };

  /**
   * Disable field input when not editing and when read only
   * @param {string} json_field json field value
   */
  const disableField = (json_field) => !isEditing || READ_ONLY_FIELDS[selectedType].includes(json_field);

  /**
   * Handle Next button click
   */
  const handleNext = () => {
    proceedToUploadExcel();
  };

  /**
   * Calls api to update data
   */
  const saveUpdatedData = async () => {
    const updatedFields = fields[selectedType].map((field) => {
      const newTitleField = fieldValue[selectedType][field.json_field];
      return {
        ...field,
        title_field: newTitleField,
        json_field: getJsonFieldName(newTitleField),
        field_autofill: fieldAutofillValues[selectedType][field.json_field],
        test_autofill: testAutofillValues[selectedType][field.json_field],
      };
    });
    const response = await updateSampleFields(selectedType, updatedFields);
    if (response.success) {
      setOriginalValueSnapshot((state) => {
        const newState = { ...state };
        newState[selectedType] = updatedFields.reduce((acc, f) => {
          acc[f.json_field] = f.title_field;
          return acc;
        }, {});
        return newState;
      });
      setOriginalAutofillSnapshot((prev) => ({
        fieldAutofill: {
          ...prev.fieldAutofill,
          [selectedType]: updatedFields.reduce((acc, f) => { acc[f.json_field] = f.field_autofill; return acc; }, {}),
        },
        testAutofill: {
          ...prev.testAutofill,
          [selectedType]: updatedFields.reduce((acc, f) => { acc[f.json_field] = f.test_autofill; return acc; }, {}),
        },
      }));
    }
    return response;
  };

  /**
   * Computes a list of changed fields (title renames + autofill toggles) for the confirmation diff
   */
  const computeUpdateDiff = () => {
    const changes = [];
    (fields[selectedType] || []).forEach((f) => {
      const { json_field } = f;
      const origTitle = originalValueSnapshot[selectedType][json_field];
      const newTitle = fieldValue[selectedType][json_field];
      const origFieldAutofill = originalAutofillSnapshot.fieldAutofill[selectedType][json_field];
      const newFieldAutofill = fieldAutofillValues[selectedType][json_field];
      const origTestAutofill = originalAutofillSnapshot.testAutofill[selectedType][json_field];
      const newTestAutofill = testAutofillValues[selectedType][json_field];

      if (origTitle !== newTitle) {
        changes.push({ label: origTitle, from: origTitle, to: newTitle });
      }
      if (origFieldAutofill !== newFieldAutofill) {
        changes.push({
          label: `${origTitle} — Field Autofill`,
          from: origFieldAutofill === "1" ? "ON" : "OFF",
          to: newFieldAutofill === "1" ? "ON" : "OFF",
        });
      }
      if (origTestAutofill !== newTestAutofill) {
        changes.push({
          label: `${origTitle} — Test Autofill`,
          from: origTestAutofill === "1" ? "ON" : "OFF",
          to: newTestAutofill === "1" ? "ON" : "OFF",
        });
      }
    });
    return changes;
  };

  /**
   * Shows the update confirmation dialog with a before/after diff of changed fields
   */
  const handleUpdateClick = () => {
    const currentErrors = fieldErrors[selectedType] || {};
    const hasErrors = Object.values(currentErrors).some(Boolean);
    if (hasErrors) {
      toast.error("Invalid inputs!");
      return;
    }
    const diff = computeUpdateDiff();
    if (diff.length === 0) {
      setIsEditing(false);
      return;
    }
    setUpdateDiff(diff);
    setShowUpdateConfirmation(true);
  };

  /**
   * Saves changes after the user confirms the update dialog
   */
  const handleUpdate = async () => {
    setLoadingOnSave(true);
    try {
      const response = await saveUpdatedData();
      if (response.success) {
        toast.success(response.message || "Updated successfully");
        await fetchFields();
        setIsEditing(false);
        setShowUpdateConfirmation(false);
      } else {
        toast.error(response.message);
        setShowUpdateConfirmation(false);
        // Stay in edit mode so the user can see the error and decide to Cancel
      }
    } finally {
      setLoadingOnSave(false);
    }
  };

  const isProduct = selectedType === Specstype.Product;

  // Table column definitions for the flat Ant Design table
  const columns = [
    {
      title: "Title",
      key: "title",
      width: "37%",
      render: (_, record) => (
        <div>
          {!disableField(record.json_field) && (
            <p className="UpdateFields__FieldTitle">
              {originalValueSnapshot[selectedType][record.json_field]}
            </p>
          )}
          <Input
            className="UpdateFields__FieldInput"
            value={fieldValue[selectedType][record.json_field] ?? ""}
            onChange={handleChange(record.json_field)}
            onKeyDown={onKeyDown(record.json_field)}
            disabled={disableField(record.json_field)}
            onPaste={onPaste(record.json_field)}
          />
          {!disableField(record.json_field) && fieldErrors[selectedType]?.[record.json_field] && (
            <p className="UpdateFields__FieldError">
              {fieldErrors[selectedType][record.json_field]}
            </p>
          )}
        </div>
      ),
    },
    {
      title: "Field Autofill",
      key: "field_autofill",
      width: "19%",
      align: "center",
      render: (_, record) => {
        const checked = fieldAutofillValues[selectedType][record.json_field] === "1";
        if (disableField(record.json_field)) {
          return checked
            ? <CheckCircleOutlined className="UpdateFields__IconCheck" />
            : <MinusOutlined className="UpdateFields__IconMinus" />;
        }
        return (
          <Switch
            checked={checked}
            onChange={(val) => handleFieldAutofillChange(record.json_field, val)}
          />
        );
      },
    },
    {
      title: "Test Autofill",
      key: "test_autofill",
      width: "19%",
      align: "center",
      render: (_, record) => {
        const checked = testAutofillValues[selectedType][record.json_field] === "1";
        if (disableField(record.json_field)) {
          return checked
            ? <CheckCircleOutlined className="UpdateFields__IconCheck" />
            : <MinusOutlined className="UpdateFields__IconMinus" />;
        }
        return (
          <Switch
            checked={checked}
            onChange={(val) => handleTestAutofillChange(record.json_field, val)}
          />
        );
      },
    },
    ...(isProduct ? [{
      title: "Link Report",
      key: "link_report",
      width: "16%",
      align: "center",
      render: (_, record) => {
        const checked = Array.isArray(updatedLinkReport) && updatedLinkReport.includes(record.json_field);
        return checked
          ? <CheckCircleOutlined className="UpdateFields__IconCheck" />
          : <MinusOutlined className="UpdateFields__IconMinus" />;
      },
    }] : [{
      title: "Link Pattern",
      key: "link_pattern",
      width: "16%",
      align: "center",
      render: (_, record) => {
        const checked = Array.isArray(updatedLinkPatternFields) && updatedLinkPatternFields.includes(record.json_field);
        return checked
          ? <CheckCircleOutlined className="UpdateFields__IconCheck" />
          : <MinusOutlined className="UpdateFields__IconMinus" />;
      },
    }]),
    {
      title: "Sample ID",
      key: "sample_id",
      width: "12%",
      align: "center",
      render: (_, record) => {
        const checked = Array.isArray(sampleIdFields[selectedType]) && sampleIdFields[selectedType].includes(record.json_field);
        return checked
          ? <CheckCircleOutlined className="UpdateFields__IconCheck" />
          : <MinusOutlined className="UpdateFields__IconMinus" />;
      },
    },
  ];

  return (
    <>
      <div className="UpdateFields">
        <p className="UpdateFields__LabelText">Select type for which you want to upload excel?</p>
        <div className="UpdateFields__TabButtonContainer">
          {[Specstype.Product, Specstype.Environment].map((type) => (
            <Button
              type="primary"
              ghost={selectedType !== type}
              htmlType="button"
              onClick={() => handleChangeType(type)}
              disabled={showUpdateConfirmation}
              key={type}
            >
              {type}
            </Button>
          ))}
        </div>
        <div className="UpdateFields__FieldsContainer">
          {loading ? (
            <div className="UpdateFields__LoadingContainer">
              <LoadingDots />
            </div>
          ) : (
            <>
              <div className="UpdateFields__FieldHeader">
                <p className="UpdateFields__LabelText">
                  {selectedType === "product" ? "Product Sample Submission Form Fields" : "Environment Sample Submission Form Fields"}
                </p>
                <div className="UpdateFields__EditButtonContainer">
                  <Button
                    type="default"
                    onClick={() => setIsEditing(true)}
                    icon={<EditIcon className="UpdateFields__EditIcon" />}
                    disabled={isEditing || fields[selectedType].length === 0}
                  >
                    Edit
                  </Button>
                </div>
              </div>
              <StyledScrollbar
                disableTrackYWidthCompensation
                disableTracksWidthCompensation
                id="updateFieldsScrollBar"
                className="UpdateFields__ScrollBar"
              >
                <Table
                  dataSource={fields[selectedType]}
                  columns={columns}
                  rowKey="json_field"
                  pagination={false}
                  rootClassName="UpdateFields__Table"
                />
              </StyledScrollbar>
            </>
          )}
        </div>
      </div>
      <div className="UpdateFields__ButtonContainer">
        {isEditing ? (
          <>
            <Button
              type="default"
              onClick={handleCancelEdit}
              disabled={loadingOnSave || showUpdateConfirmation}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUpdateClick}
              loading={loadingOnSave}
            >
              Update
            </Button>
          </>
        ) : (
          <>
            <Button
              type="default"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleNext}
              disabled={fields[selectedType].length === 0}
            >
              Next
            </Button>
          </>
        )}
      </div>
      {showUpdateConfirmation && (
        <Modal
          open
          centered
          destroyOnClose
          maskClosable={false}
          closable={false}
          width={480}
          title="Update fields"
          okText="Update"
          cancelText="Cancel"
          confirmLoading={loadingOnSave}
          cancelButtonProps={{ disabled: loadingOnSave }}
          onOk={handleUpdate}
          onCancel={() => setShowUpdateConfirmation(false)}
        >
          <p>Are you sure you want to update these fields?</p>
          {updateDiff.length > 0 && (
            <ul className="UpdateFields__ConfirmDiff">
              {updateDiff.map((change) => (
                <li key={change.label} className="UpdateFields__ConfirmDiffItem">
                  <span className="UpdateFields__ConfirmDiffLabel">{`${change.label}:`}</span>
                  {" "}
                  <span className="UpdateFields__ConfirmDiffFrom">{change.from}</span>
                  {" → "}
                  <span className="UpdateFields__ConfirmDiffTo">{change.to}</span>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
}
